import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { GridColDef } from '@mui/x-data-grid';
import { showLabel } from '@/utils/helper';
import { QualityType } from '@/api/enum/common.enum';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { BOX_SERIES_TYPE, PUBLISHED_LISTB } from '@/common/constants/common';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import {
	useGetWeekTreasureListQuery,
	useUpdateWeekTreasureMutation,
} from '@/api/exchange/weekTreasureApiSlice';
import {
	WeeklyGameDTO,
	WeekTreasureFilter,
} from '@/api/DTO/exchange/weekTreasure.interface';
import PublishedChip from '@/common/components/PublishedChip';

interface Props {}

export default function AddTreasureDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [selectedData, setSelectedData] = useState<any[]>([]);
	const [updateWeekTreasure] = useUpdateWeekTreasureMutation();

	const qualityList = [
		{ id: QualityType.Normal, name: t('chartletList.normal') },
		{ id: QualityType.Rare, name: t('chartletList.rare') },
		{ id: QualityType.Precious, name: t('chartletList.precious') },
		{ id: QualityType.SuperPrecious, name: t('chartletList.superPrecious') },
	];
	const defaultPropertyFilter: WeekTreasureFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		gameTypeList: [],
		publishedList: [],
		qualityList: [],
		selectedList: [0, 1],
	};
	const defaultValues: DefaultValues<WeekTreasureFilter> =
		defaultPropertyFilter;
	const methods = useForm<WeekTreasureFilter>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetWeekTreasureListQuery(methods.getValues(), {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	const { data: allData } = useGetWeekTreasureListQuery(
		{
			sortings: [],
			selectedList: [0, 1],
			pagination: {
				page: 1,
				size: 999999,
			},
			publishedList: [],
			qualityList: [],
		},
		{
			skip: !open,
		}
	);

	useEffect(() => {
		if (allData) {
			const selectedRowsData = allData?.data?.items.filter(
				(row) => row.selected
			);
			setSelectedData(selectedRowsData.map((item) => item.gameTreasureId));
		}
	}, [open]);

	useEffect(() => {}, [methods.watch()]);
	const onSubmit = async () => {
		const response = await updateWeekTreasure(selectedData);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.fixTreasureSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		methods.reset(defaultPropertyFilter);
		setSelectedData([]);
		setOpen(false);
	};
	const handleOpen = () => {
		setOpen(true);
	};

	const columns: GridColDef[] = [
		{
			field: 'gameTypeName',
			headerName: t('box.typeName'),
			flex: 10,
		},
		{
			field: 'gameSeriesName',
			headerName: t('box.seriesName'),
			flex: 10,
		},
		{
			field: 'published',
			headerName: t('box.seriesStatus'),
			flex: 10,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'gameTreasureName',
			headerName: t('box.treasureName'),
			flex: 10,
		},
		{
			field: 'quality',
			headerName: t('chartletList.quality'),
			flex: 10,
			renderCell: (params: any) => showLabel(params.row.quality, qualityList),
		},
		{
			field: 'costPoints',
			headerName: t('chartletList.integral'),
			flex: 10,
		},
	];

	const handlechange = (text: string) => {
		methods.reset({ ...defaultPropertyFilter, searchString: text });
	};

	const handleSelect = (rows: string[]) => {
		setSelectedData(rows || []);
	};

	const transformResponse = useCallback((response?: WeeklyGameDTO[]) => {
		if (!response) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				// updatedAt: !item.updatedAt
				// 	? '-'
				// 	: dayjs(item.updatedAt)
				// 			.locale('en')
				// 			.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});

		return newResponse;
	}, []);

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<NoteAltOutlinedIcon />}
				onClick={() => handleOpen()}
			>
				{t('weekTreasure.editTreasure')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('weekTreasure.assignTreasure')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Box
						display='flex'
						justifyContent='space-between'
						alignItems='center'
						marginBottom={'10px'}
					>
						<Typography
							variant='body2'
							sx={{
								display: 'flex',
							}}
						>
							<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
							&nbsp;
							{t('box.showTreasureTips')}
						</Typography>
					</Box>
					<FormProvider {...methods}>
						<Grid container rowSpacing={2} columnSpacing={1}>
							<Grid item xs={3}>
								<AppSummarySearchBar
									name='searchString'
									control={methods.control}
									placeholder={t('common.search')}
									onchange={(text) => {
										handlechange(text);
									}}
								/>
							</Grid>

							<Grid item xs={3}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='gameTypeList'
									label={t('box.treasureType')}
									options={BOX_SERIES_TYPE}
									size='small'
									emptyArrayIfNoSelection
								/>
							</Grid>
							<Grid item xs={3}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='publishedList'
									label={t('box.seriesStatus')}
									options={PUBLISHED_LISTB}
									size='small'
									emptyArrayIfNoSelection
								/>
							</Grid>
							<Grid item xs={3}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='qualityList'
									label={t('chartletList.quality')}
									options={qualityList}
									size='small'
									emptyArrayIfNoSelection
								/>
							</Grid>
						</Grid>
						<div style={{ height: '20px' }}></div>
						<AppDataGrid
							checkboxSelection
							loading={!isGetPropertySummarySuccess}
							rows={transformResponse(propertySummary?.data?.items) ?? []}
							rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
							columns={columns}
							getRowId={(row) => row.gameTreasureId}
							onSelectionModelChange={(selectionModel) => {
								handleSelect(selectionModel as string[]);
							}}
							// selectionModel={selectedData.map(
							// 	(item: { gameTreasureId: any }) => item.gameTreasureId
							// )}
							selectionModel={selectedData}
						/>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('box.change')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
